import React, { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import moment from 'moment'
import { IFibaRequest, IFibaResponse } from '../models/model'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../base/customHooks/componentHOC'
import { useCustomReducer } from '../../../../base/customHooks'
import Table from '../../../../components/Table'
import IPModal from '../../../../components/IPModal/IPModal'
import { GetFibaListApi } from '../services/getFibaListApi'

interface IFibaInsuranceListState {
  dealerCode: string
  showHistory: boolean
  historyData: IFibaResponse[]
  selectedPoliceId: string
}

function Index(props: BaseComponentWithContextProps) {

  const getInitialState = (): IFibaInsuranceListState => {
    return {
      dealerCode: '',
      showHistory: false,
      historyData: [],
      selectedPoliceId: '',
    }
  }
  const intialState = getInitialState()
  const [state, setState] = useCustomReducer<IFibaInsuranceListState>(intialState)

  const [fibaList, setFibaList] = useState<IFibaResponse[]>([])

  const getFibaList = () => {
    props.showLoading();

    const fibaApi: GetFibaListApi = new GetFibaListApi({})

    const req: IFibaRequest = {
      DealerCode: state.dealerCode,
    }
    try {
      fibaApi.getFibaList(req).then(res => {
        if (res) {
          setFibaList(res)
        } else {
          setFibaList([])
        }
        props.hideLoading();
      }).catch(err => {
        props.hideLoading();
        Swal.fire({
          icon: 'error',
          title: err?.description || 'Fiba sigorta listesi alınırken bir hata oluştu.',
          showConfirmButton: true,
          allowOutsideClick: false,
        });
      })
    } catch (err: any) {
      props.hideLoading();
      Swal.fire({
        icon: 'error',
        title: err?.description || 'Fiba sigorta listesi alınırken bir hata oluştu.',
        showConfirmButton: true,
        allowOutsideClick: false,
      });
    }
  };

  useEffect(() => {
    getFibaList()
  }, [])

  // Bir hareketin güncellik sırasını Tanzim Tarihi + Onay Saati'ne göre verir (örn: 11.07.2026 12:59)
  // Tarih/saat parse edilemezse Id'ye düşer
  const getSortValue = (item: IFibaResponse): number => {
    const datePart = item.TanzimTarihi
    const timePart = item.Onaysaatdakika ? (item.Onaysaatdakika + '0').toString().slice(0, 5) : ''
    if (datePart) {
      const withTime = moment(`${datePart} ${timePart}`.trim(), ['DD.MM.YYYY HH:mm', 'DD.MM.YYYY'])
      if (withTime.isValid()) return withTime.valueOf()
    }
    return Number(item.Id) || 0
  }

  // Başlama Tarihi'ni (DD.MM.YYYY) karşılaştırılabilir bir değere çevirir
  const getBaslamaValue = (item: IFibaResponse): number => {
    if (item.BaslamaTarihi) {
      const m = moment(item.BaslamaTarihi, 'DD.MM.YYYY')
      if (m.isValid()) return m.valueOf()
    }
    return 0
  }

  // Her poliçe için en yeni kayıtlı hareketi (son durum) döndürür; ana liste Başlama Tarihi'ne göre (yeniden eskiye) sıralanır
  const latestByPolice = useMemo(() => {
    const map = new Map<string, IFibaResponse>()
    fibaList.forEach(item => {
      const existing = map.get(item.PoliceId)
      if (!existing || getSortValue(item) >= getSortValue(existing)) {
        map.set(item.PoliceId, item)
      }
    })
    return Array.from(map.values()).sort((a, b) => getBaslamaValue(b) - getBaslamaValue(a))
  }, [fibaList])

  // Poliçe başına hareket sayısı - tarihçe ikonunu yalnızca birden fazla harekette göstermek için
  const policeCountMap = useMemo(() => {
    const map = new Map<string, number>()
    fibaList.forEach(item => {
      map.set(item.PoliceId, (map.get(item.PoliceId) || 0) + 1)
    })
    return map
  }, [fibaList])

  // Seçilen poliçenin tüm hareketlerini yeniden eskiye sıralayıp modalda açar
  const showPoliceHistory = (policeId: string) => {
    const history = fibaList
      .filter(item => item.PoliceId === policeId)
      .sort((a, b) => getSortValue(b) - getSortValue(a))
    setState({ showHistory: true, historyData: history, selectedPoliceId: policeId })
  }

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Tarihçe',
        accessor: 'Tarihce',
      },
      {
        Header: '#',
        accessor: 'order',
      },
      {
        Header: 'Poliçe No',
        accessor: 'PoliceId',
      },
      {
        Header: 'Başlama Tarihi',
        accessor: 'BaslamaTarihi',
      },
      {
        Header: 'TCKN',
        accessor: 'Tckn',
      },
      {
        Header: 'Ad-Soyad',
        accessor: 'Adsoyad',
      },

      {
        Header: 'Brüt',
        accessor: 'Brut',
      },
      {
        Header: 'Uzatılmış Garanti Süresi',
        accessor: 'Uzatilmisgarantisuresi',
      },
      {
        Header: 'Poliçe Hareket ID',
        accessor: 'PoliceHareketId',
      },
      {
        Header: 'Paket Adı',
        accessor: 'PaketAdi',
      },
      {
        Header: 'Durum Açıklama',
        accessor: 'DurumAciklama',
      },
      {
        Header: 'Ek Belge Açıklama',
        accessor: 'EkbelgeAciklama',
      },
      {
        Header: 'Tanzim Tarihi',
        accessor: 'TanzimTarihi',
      },
      {
        Header: 'Onay Saat-Dakika',
        accessor: 'Onaysaatdakika',
      },
      {
        Header: 'Ürün Kategori Adı',
        accessor: 'Urunkategoriadi',
      },
      {
        Header: 'Cihaz Markası',
        accessor: 'CihazMarkasi',
      },
      {
        Header: 'Model Bilgisi',
        accessor: 'ModelBilgisi',
      },
      {
        Header: 'Cihaz Bedeli',
        accessor: 'CihazBedeli',
      },

      {
        Header: 'IMEI No',
        accessor: 'Imeino',
      },
      {
        Header: 'Personel',
        accessor: 'Personel',
      },
      {
        Header: 'Seri No',
        accessor: 'Serino',
      },
    ],
    []
  )


  const tableData = useMemo(
    () =>
      latestByPolice.map((item, index) => {
        const data = {
          order: index + 1,
          PoliceId: item.PoliceId,
          BaslamaTarihi: item.BaslamaTarihi,
          Tckn: item.Tckn,
          Adsoyad: item.Adsoyad,
          Uzatilmisgarantisuresi: item.Uzatilmisgarantisuresi,
          Brut: item.Brut,
          PoliceHareketId: item.PoliceHareketId,
          DurumAciklama: item.DurumAciklama,
          EkbelgeAciklama: item.EkbelgeAciklama,
          TanzimTarihi: item.TanzimTarihi,
          Onaysaatdakika: (item.Onaysaatdakika + "0").toString().slice(0, 5),
          Urunkategoriadi: item.Urunkategoriadi,
          Personel: item.Personel,
          Serino: item.Serino,
          Imeino: item.Imeino,
          CihazMarkasi: item.CihazMarkasi,
          ModelBilgisi: item.ModelBilgisi,
          PaketAdi: item.PaketAdi == "1" ? "Ekran Kırılması" : item.PaketAdi == "2" ? "Cihaz Koruma" : "",
          CihazBedeli: item.CihazBedeli,
          Tarihce:
            (policeCountMap.get(item.PoliceId) || 0) > 1 ? (
              <a
                role='button'
                style={{ cursor: 'pointer', color: 'blue' }}
                data-toggle='tooltip'
                data-original-title='Poliçe hareket geçmişi'
                onClick={() => showPoliceHistory(item.PoliceId)}
              >
                <i className='icon-clock' style={{ fontSize: '1.2rem', color: 'blue' }}></i>
              </a>
            ) : (
              ''
            ),
        }
        return data
      }),
    [latestByPolice, policeCountMap]
  )

  return (
    <div className=''>
      <div className='content col-lg-12 m-auto'>
        <div className=''>
          <div className='card-header d-flex justify-content-between align-items-center mb-2'>
            <div className='col-lg-10 d-flex align-items-center'>
              <h4>Fiba Sigorta Listesi</h4>
            </div>
          </div>
          <div className='card-body'>
            <div className='row'>
              <div className='col-lg-12'>
                <div className='row'>
                  <div className='col-sm-12'>
                    <Table columns={tableColumns} data={tableData} currentPage={0} />
                    <IPModal
                      show={state.showHistory}
                      onClose={() => setState({ showHistory: false, historyData: [], selectedPoliceId: '' })}
                      hideConfirm={true}
                      width={1400}
                      title={`Poliçe Hareket Geçmişi - ${state.selectedPoliceId}`}
                    >
                      <table className='table'>
                        <thead>
                          <tr role='row' className='odd'>
                            <th>Tanzim Tarihi</th>
                            <th>Onay Saati</th>
                            <th>Durum</th>
                            <th>Ek Belge Açıklama</th>
                            <th>Brüt</th>
                            <th>Poliçe Hareket ID</th>
                            <th>Personel</th>
                          </tr>
                        </thead>
                        <tbody>
                          {state.historyData &&
                            state.historyData.length > 0 &&
                            state.historyData.map(his => (
                              <tr role='row' className='odd' key={his.Id}>
                                <td>{his.TanzimTarihi}</td>
                                <td>{his.Onaysaatdakika ? (his.Onaysaatdakika + '0').toString().slice(0, 5) : ''}</td>
                                <td>{his.DurumAciklama}</td>
                                <td>{his.EkbelgeAciklama}</td>
                                <td>{his.Brut}</td>
                                <td>{his.PoliceHareketId}</td>
                                <td>{his.Personel}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </IPModal>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(Index)